import React from 'react';
import {
  Container,
  Grid,
  CircularProgress,
  Typography,
  Box,
  Card,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Button,
  Divider,
} from '@mui/material';
import { Icon } from '@iconify/react';
import moment from 'moment';
import Page from 'src/components/Page';
import { useNavigate, useParams } from 'react-router-dom';
import { financialSimulationService } from 'src/services/financial-simulation.service';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'src/context/auth/AuthContext';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';

function getStatusChip(status?: string) {
  switch (status) {
    case 'Completed':
      return {
        label: 'Completed',
        color: '#059669',
        bgcolor: 'rgba(16, 185, 129, 0.12)',
        icon: 'solar:check-circle-bold-duotone',
      };
    case 'Overdue':
      return {
        label: 'Overdue',
        color: '#DC2626',
        bgcolor: 'rgba(239, 68, 68, 0.12)',
        icon: 'solar:alarm-bold-duotone',
      };
    case 'In Progress':
    default:
      return {
        label: 'In Progress',
        color: '#D97706',
        bgcolor: 'rgba(245, 158, 11, 0.12)',
        icon: 'solar:hourglass-line-bold-duotone',
      };
  }
}

export default function ViewFinancialSimulations() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getBaseRoute } = useAuth();
  const backRoute = getBaseRoute('/financial-simulations');

  const {
    data: financialSimulation,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['financialSimulation', id],
    queryFn: () => financialSimulationService.getChallengeById(id),
    enabled: !!id,
  });

  const scenario = financialSimulation?.scenario?.title || 'Whammy Challenge';
  const status = financialSimulation?.status;
  const duration = financialSimulation?.duration;
  const amount = Number(financialSimulation?.amount ?? financialSimulation?.scenario?.amount ?? 0);
  const startDate = financialSimulation?.createdat;
  const student = financialSimulation?.student;
  const studentId = student?.id || financialSimulation?.student_id || (financialSimulation as any)?.studentid;
  const studentName = `${student?.user?.firstname || ''} ${student?.user?.lastname || ''}`.trim() || 'Scholar';
  const loanRequest = financialSimulation?.loan;

  useHeaderBreadcrumbs({
    backUrl: backRoute,
    backLabel: 'Whammies',
    breadcrumbs: [
      { label: 'Whammies', path: backRoute },
      ...(scenario ? [{ label: scenario, isPill: true }] : []),
      { label: 'Challenge Details' },
    ],
  });

  if (isLoading) {
    return (
      <Page title="Whammy Details | Gold Minds Portal">
        <Container maxWidth="lg" sx={{ py: 10, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontWeight: 600 }}>
            Loading Whammy details...
          </Typography>
        </Container>
      </Page>
    );
  }

  if (error || !financialSimulation) {
    return (
      <Page title="Whammy Details | Gold Minds Portal">
        <Container maxWidth={false} sx={{ py: 6 }}>
          <Card sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider', maxWidth: 480, mx: 'auto' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: 'rgba(239, 68, 68, 0.12)',
                color: '#DC2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1.5,
              }}
            >
              <Icon icon="solar:danger-triangle-bold" width={24} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
              Whammy Challenge Not Found
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
              The requested simulation challenge record could not be loaded.
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(backRoute)}
              sx={{ borderRadius: 1.75, textTransform: 'none', fontWeight: 700, px: 2, py: 0.5 }}
            >
              Return to Whammies Hub
            </Button>
          </Card>
        </Container>
      </Page>
    );
  }

  const statusMeta = getStatusChip(status);

  return (
    <Page title={`${scenario} | Whammy Details`}>
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5, pb: 4 }}>
        {/* Navigation Bar */}
        <Box sx={{ mb: 2, mt: 0.25 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <IconButton
              size="small"
              onClick={() => navigate(backRoute)}
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.75,
                p: 0.75,
              }}
            >
              <Icon icon="solar:arrow-left-linear" width={18} />
            </IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: -0.3, fontSize: '1.35rem' }}>
                  {scenario}
                </Typography>
                <Chip
                  icon={<Icon icon={statusMeta.icon} width={13} />}
                  label={statusMeta.label}
                  size="small"
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: '0.6875rem',
                    color: statusMeta.color,
                    bgcolor: statusMeta.bgcolor,
                    border: `1px solid ${statusMeta.color}22`,
                    '& .MuiChip-icon': {
                      color: `${statusMeta.color} !important`,
                      marginLeft: '4px',
                    },
                  }}
                />
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                Active simulation details and resolution tracker for scholar {studentName}.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Grid container spacing={2}>
          {/* Main Challenge Card */}
          <Grid item xs={12} md={7}>
            <Card
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, fontSize: '0.95rem' }}>
                Scenario Overview
              </Typography>

              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.6, fontSize: '0.8125rem' }}>
                {financialSimulation?.scenario?.description ||
                  'No detailed description available for this simulation challenge.'}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                    Penalty Due
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#DC2626', mt: 0.25, fontSize: '1.15rem' }}>
                    ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                    Allowed Duration
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', mt: 0.25, fontSize: '1.15rem' }}>
                    {duration ? `${duration} Days` : 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                    Assigned Date
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.25, fontSize: '0.95rem' }}>
                    {startDate ? moment(startDate).format('MMM DD, YYYY') : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Card>

            {/* Resolution Progress / Timeline */}
            <Card
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                mt: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, fontSize: '0.95rem' }}>
                Resolution Milestones
              </Typography>

              <Stack spacing={2}>
                {/* Step 1: Challenge Issued */}
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      bgcolor: 'rgba(245, 158, 11, 0.15)',
                      color: '#D97706',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon icon="solar:danger-triangle-bold" width={16} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem' }}>
                      Whammy Challenge Issued
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {startDate ? moment(startDate).format('MMMM DD, YYYY [at] hh:mm A') : 'Recorded'}
                    </Typography>
                  </Box>
                </Stack>

                {/* Step 2: Loan Requested (Optional) */}
                {loanRequest && (
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        bgcolor: 'rgba(59, 130, 246, 0.15)',
                        color: '#2563EB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon icon="solar:hand-money-bold" width={16} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem' }}>
                        Scholar Requested Loan Assistance
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                        {loanRequest?.createdat ? moment(loanRequest.createdat).format('MMMM DD, YYYY [at] hh:mm A') : 'Recorded'}
                      </Typography>
                    </Box>
                  </Stack>
                )}

                {/* Step 3: Resolution / Completion */}
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      bgcolor: status === 'Completed' ? 'rgba(16, 185, 129, 0.15)' : 'grey.100',
                      color: status === 'Completed' ? '#059669' : 'text.disabled',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon icon={status === 'Completed' ? 'solar:check-circle-bold' : 'solar:clock-circle-bold'} width={16} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem' }}>
                      {status === 'Completed' ? 'Challenge Resolved' : 'Resolution Pending'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {status === 'Completed'
                        ? 'Scholar successfully settled expense balance.'
                        : 'Awaiting scholar payment or loan disbursement.'}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Card>
          </Grid>

          {/* Scholar Information Card */}
          <Grid item xs={12} md={5}>
            <Card
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5, display: 'block', fontSize: '0.6875rem' }}>
                Assigned Scholar
              </Typography>

              <Card
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    mb: 2,
                    cursor: studentId ? 'pointer' : 'default',
                    '&:hover .scholar-name': {
                      color: studentId ? 'primary.main' : 'text.primary',
                      textDecoration: studentId ? 'underline' : 'none',
                    },
                    '&:hover .scholar-avatar': {
                      transform: studentId ? 'scale(1.06)' : 'none',
                    },
                  }}
                  onClick={() => {
                    if (studentId) navigate(getBaseRoute(`/view-student/${studentId}`));
                  }}
                >
                  <Avatar
                    className="scholar-avatar"
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: 'primary.light',
                      color: 'primary.dark',
                      fontWeight: 800,
                      fontSize: '0.95rem',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {studentName.charAt(0)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography className="scholar-name" variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.875rem', transition: 'color 0.15s ease' }}>
                      {studentName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {student?.user?.email || 'No email provided'}
                    </Typography>
                  </Box>
                  {studentId && (
                    <IconButton size="small" sx={{ color: 'primary.main', bgcolor: 'rgba(46, 125, 50, 0.08)' }}>
                      <Icon icon="solar:arrow-right-up-linear" width={16} />
                    </IconButton>
                  )}
                </Stack>

                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                    Current Balance
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 900,
                      fontSize: '1.1rem',
                      color: Number(student?.balance || 0) < amount ? '#DC2626' : '#059669',
                    }}
                  >
                    ${Number(student?.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>

                {studentId && (
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<Icon icon="solar:user-bold-duotone" width={16} />}
                    onClick={() => navigate(getBaseRoute(`/view-student/${studentId}`))}
                    sx={{
                      mt: 1.5,
                      borderRadius: 1.5,
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: 'divider',
                      color: 'text.primary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    View Scholar Profile
                  </Button>
                )}
              </Card>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
